import { useCallback, useEffect, useMemo, useState } from "react";

import { Link as RouterLink } from "react-router-dom";
import { Link, Typography } from "@mui/material";
import { type FormikProps } from "formik";

import { useApiContext } from "../../api/context";
import useMountEffect from "../../Components/hooks/useMountEffect";
import { useCustomerContext } from "../../Context/CustomerContext";
import { isCustomerInPresentationMode } from "../../Context/useCustomerOrPresentationModeCustomer";
import { useConcedefy } from "../../Pages/MPA/hooks/useConcedefy";
import useCreateTicket, { type ticketFields } from "../../Pages/MPA/hooks/useCreateTicket";
import {
  setZdTicketId,
  useCaseIQByDetails,
  useCaseIQByID,
  useCaseIQForAddTicketId,
} from "../../Support/Components/CaseIQ/utils";
import { useIsDCIEssentialsTier } from "../../Support/hooks/useIsDCIEssentialsTier";
import { consoleErrorWithSentry } from "../../utils";
import useSegmentTrackEvent from "../../utils/useSegmentTrackEvent";
import { useSnackbar, useSuccessSnackbar } from "../SharedSnackbar/SharedSnackbar.context";
import { useAvaCaseIQContext } from "./AvaCaseIQProvider";
import { useAvaDialogContext } from "./AvaDialogContextProvider";
import { useTrpcContext } from "./AvaTRPCContext";
import { filterAssetsByPlatform, useGroupedAssets } from "./hooks";
import { type CaseIQFormikValues, type CaseIQPayload, type CaseIQResponse, type CaseIQTechnicalDetails } from "./types";
import { checkIfCanOpenUrgentTicket, isJapanese, removeBase64ImagesFromContent } from "./utils";

export const useCreateCaseIQTicket = () => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [concedefyEmail, setConcedefyEmail] = useState("");

  const { createTicket } = useCreateTicket();
  const showSuccessSnackbar = useSuccessSnackbar();
  const snackbar = useSnackbar();

  const { customer, isProductOnlyCustomer } = useCustomerContext({
    allowNull: true,
  });

  const isInPresentationMode = customer && isCustomerInPresentationMode(customer);
  const { accountManagerEmails, billingProfile } = useAvaCaseIQContext();
  const api = useApiContext();
  const { summarizeDescription } = useTrpcContext();
  const caseIQForAddTicketId = useCaseIQForAddTicketId();
  const { handleCloseAvaDialog } = useAvaDialogContext();

  const { getUserDetails } = useConcedefy();
  const { trackEvent } = useSegmentTrackEvent();

  const onSubmit = useCallback(
    async (caseIQState: CaseIQFormikValues) => {
      const { content, platform, cloudProduct, asset, priority, requester, callMe } = caseIQState;

      setSubmitting(true);

      let title = "No title";
      try {
        const { title: summary } = await summarizeDescription(removeBase64ImagesFromContent(content));
        title = summary;
      } catch (e) {
        consoleErrorWithSentry(e);
      }

      try {
        const tags = [...(cloudProduct?.tags ?? []), "ava-case-iq"];
        if (isJapanese(content)) {
          tags.push("lang:ja");
          tags.push("lang-acpt:ja");
        }

        if (callMe) {
          tags.push("vc:callback_requested");
        }

        if (isProductOnlyCustomer) {
          tags.push("saas_user");
        }

        const ticketDetails = {
          platformInfoField: asset?.accountId ?? asset?.identifier,
          platformField: platform as ticketFields["platformField"],
          productField: cloudProduct?.name,
          priority: priority as ticketFields["priority"],
          subject: title,
          tags,
          description: content,
          "collaborators[]": accountManagerEmails,
          billingProfile,
          userEmail: requester,
          isInPresentationMode: isInPresentationMode ?? false,
        };

        trackEvent("Case IQ ticket Submitted", {
          source: "Ava",
          feature: "support ticket",
          ...ticketDetails,
        });

        const response = await createTicket(customer?.id ?? "", ticketDetails);

        if (response.data.id) {
          setZdTicketId(String(response.data.id));
          caseIQForAddTicketId({
            description: content,
            userSelectedFields: {
              platform,
              product: cloudProduct?.id,
              assetId: asset?.identifier,
              severity: priority,
            },
          });
        }

        const userDetails = await getUserDetails(customer?.id ?? "");
        if (response.data.id && !userDetails.data.organization.organization_fields.concedefy_disabled) {
          const projectId = new FormData();
          projectId.append("platformInfoField", asset?.identifier ?? "");
          try {
            const concedefyRes = await api.post(
              `/support/customers/${customer?.id}/concedefy/${response.data.id}`,
              projectId,
              { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (concedefyRes.data?.email) {
              setConcedefyEmail(concedefyRes.data.email);
            }
          } catch (error) {
            consoleErrorWithSentry(error);
          }
        }

        setSubmitted(true);
        showSuccessSnackbar(
          <Typography variant="body1">
            Your support message has been sent. View your ticket{" "}
            <Link
              color="inherit"
              component={RouterLink}
              to={`/customers/${customer?.id}/support/tickets/${response.data.id}`}
              onClick={handleCloseAvaDialog}
              underline="always"
              variant="body1"
            >
              here
            </Link>
          </Typography>
        );
      } catch (e) {
        snackbar.onOpen({
          message: "Unable to create request ticket",
          variant: "error",
          autoHideDuration: 30000,
          withClose: true,
        });
      }
      setSubmitting(false);
    },
    [
      createTicket,
      customer?.id,
      snackbar,
      api,
      getUserDetails,
      showSuccessSnackbar,
      summarizeDescription,
      trackEvent,
      accountManagerEmails,
      caseIQForAddTicketId,
      billingProfile,
      handleCloseAvaDialog,
      isProductOnlyCustomer,
      isInPresentationMode,
    ]
  );

  return { isSubmitting, onSubmit, submitted, concedefyEmail };
};

let prevContent = "";

export const useCaseIQFields = (
  localContentToSend: string,
  formik: FormikProps<CaseIQFormikValues>,
  initialTechnicalDetails: CaseIQTechnicalDetails[]
) => {
  const { categoryList } = useAvaCaseIQContext();
  const assetsList = useGroupedAssets();
  const { caseIQConversationId } = useAvaCaseIQContext();
  const isDCIEssentials = useIsDCIEssentialsTier();

  const { platform, cloudProduct, asset, priority } = formik.values;

  const {
    platform: platformTouched,
    cloudProduct: cloudProductTouched,
    asset: assetTouched,
    priority: priorityTouched,
  } = formik.touched;

  const [caseIQDetails, setCaseIQDetails] = useState<CaseIQResponse | null>({
    technicalDetails: initialTechnicalDetails,
    requiredFields: {
      platform: "",
      severity: "",
      product: "",
      assetId: "",
    },
  });

  const { fetchCaseIQ } = useCaseIQByDetails();

  const payload: CaseIQPayload = useMemo(() => {
    const platformValue = platformTouched ? platform : undefined;
    const productValue = cloudProductTouched ? cloudProduct?.id : undefined;

    if (prevContent !== localContentToSend) {
      prevContent = localContentToSend;
    } else {
      return { description: "", userSelectedFields: {} };
    }

    return {
      description: localContentToSend,
      userSelectedFields: {
        platform: platformValue,
        product: productValue,
        subject: "",
      },
      conversationId: caseIQConversationId,
    };
  }, [caseIQConversationId, cloudProduct, localContentToSend, platform, cloudProductTouched, platformTouched]);

  useEffect(() => {
    if (payload.description) {
      fetchCaseIQ(payload).then((data) => {
        setCaseIQDetails(data);
      });
    }
  }, [fetchCaseIQ, payload]);

  useEffect(() => {
    if (!caseIQDetails?.requiredFields) return;

    const {
      platform: requiredPlatform,
      severity: requiredSeverity,
      product: requiredProduct,
      assetId: requiredAsset,
    } = caseIQDetails.requiredFields;

    const requieredCloudProduct = categoryList.find((category) => category.id === requiredProduct) || null;

    const canOpenUrgentTicket = checkIfCanOpenUrgentTicket(
      platformTouched ? platform : requiredPlatform,
      cloudProductTouched ? cloudProduct : requieredCloudProduct,
      isDCIEssentials
    );

    if (requiredPlatform && requiredPlatform !== platform && !platformTouched) {
      formik.setFieldValue("platform", requiredPlatform);
    }

    if (requiredSeverity && requiredSeverity.toLocaleLowerCase() !== priority && !priorityTouched) {
      if (requiredSeverity.toLocaleLowerCase() === "urgent" && !canOpenUrgentTicket) {
        if (priority !== "high") {
          formik.setFieldValue("priority", "high");
        }
      } else {
        formik.setFieldValue("priority", requiredSeverity.toLocaleLowerCase());
      }
    }

    if (requiredProduct && requiredProduct !== cloudProduct?.id && !cloudProductTouched) {
      if (requieredCloudProduct) {
        formik.setFieldValue("cloudProduct", requieredCloudProduct);
      }
    }

    if (requiredAsset) {
      const newAssets = filterAssetsByPlatform(assetsList, requiredPlatform);

      let found = newAssets.find((asset) => asset.identifier === requiredAsset);

      if (!found && requiredPlatform === "amazon_web_services") {
        found = newAssets.find((asset) => asset.accountId === requiredAsset);
      }

      if (requiredAsset !== asset?.identifier && requiredAsset !== asset?.accountId && !assetTouched && found) {
        formik.setFieldValue("asset", found);
      }
    }
  }, [
    caseIQDetails,
    platform,
    priority,
    cloudProduct,
    asset,
    categoryList,
    assetsList,
    platformTouched,
    priorityTouched,
    cloudProductTouched,
    assetTouched,
    formik,
    isDCIEssentials,
  ]);

  const caseIQTechnicalDetails = caseIQDetails?.technicalDetails.length ? caseIQDetails.technicalDetails : [];

  return { caseIQTechnicalDetails };
};

export const useInitializeCaseIQFields = (
  setValues: (values: CaseIQFormikValues) => void,
  caseIQConversationId: string
) => {
  const [loading, setLoading] = useState(false);
  const [initialTechnicalDetails, setInitialTechnicalDetails] = useState<CaseIQTechnicalDetails[]>([]);

  const { categoryList } = useAvaCaseIQContext();

  const assetsList = useGroupedAssets();
  const { summarizeConversation } = useTrpcContext();
  const fetchCaseIQByID = useCaseIQByID();

  useMountEffect(() => {
    if (!caseIQConversationId) return;

    const initialize = async () => {
      try {
        setLoading(true);
        const summarizePromise = summarizeConversation(caseIQConversationId).catch(() => null);
        const fetchCaseIQPromise = fetchCaseIQByID(caseIQConversationId);
        const [summaryResponse, fetchCaseIQResponse] = await Promise.all([summarizePromise, fetchCaseIQPromise]);
        if (!fetchCaseIQResponse) {
          setLoading(false);
          return;
        }
        const { platform, product, assetId, severity } = fetchCaseIQResponse.requiredFields;
        const cloudProduct = categoryList.find((category) => category.id === product) ?? null;
        const newAssets = filterAssetsByPlatform(assetsList, platform);
        const found = newAssets.find((asset) => asset.identifier === assetId);
        const caseIQState: CaseIQFormikValues = {
          content: summaryResponse?.summary ?? "",
          platform,
          cloudProduct,
          asset: found ?? null,
          priority: severity.toLowerCase(),
          requester: "",
          callMe: false,
        };
        setValues(caseIQState);
        setInitialTechnicalDetails(fetchCaseIQResponse.technicalDetails);
      } catch (error) {
        consoleErrorWithSentry(error);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  });

  return { loading, initialTechnicalDetails };
};
